import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "dark";
type Size = "sm" | "md" | "lg";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-mef-green text-white border-transparent hover:bg-mef-green-hover hover:shadow-mef-button",
  secondary:
    "bg-white text-mef-green-text border-mef-green hover:bg-mef-green-tint",
  danger:
    "bg-mef-red text-white border-transparent hover:bg-mef-red-hover",
  ghost:
    "bg-transparent text-mef-green-text border-transparent hover:bg-mef-green-tint",
  dark:
    "bg-transparent text-white border-white/20 hover:bg-white/5",
};

const SIZE: Record<Size, string> = {
  sm: "h-7 px-3 text-[10px] rounded-xs",
  md: "h-10 px-4 text-[11px] rounded-sm min-w-[140px]",
  lg: "h-11 px-5 text-[12px] rounded-sm min-w-[160px]",
};

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

export function Button({
  variant = "primary",
  size = "md",
  leadingIcon,
  trailingIcon,
  className = "",
  children,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      className={[
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium uppercase tracking-[0.02em]",
        "border-[1.2px] transition-[box-shadow,background-color,color,border-color] duration-100",
        "disabled:bg-mef-surface-2 disabled:text-black/40 disabled:border-transparent disabled:cursor-not-allowed disabled:shadow-none",
        "mef-focus-ring",
        VARIANT[variant],
        SIZE[size],
        className,
      ].join(" ")}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}
